'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { Sparkles, TrendingUp, Star, ChevronRight, Briefcase, Package, Home, Car, Hotel, Wrench } from 'lucide-react';

interface RecommendedItem {
  id: string;
  title: string;
  subtitle: string;
  price?: string;
  location?: string;
  badge?: string;
  badgeColor?: string;
  href: string;
  icon: React.ReactNode;
  iconColor: string;
  reason: string;
}

// Role-based recommendation sets
function getRecommendations(role: string, isLoggedIn: boolean): RecommendedItem[] {
  if (!isLoggedIn) {
    return [
      { id: 'r1', title: 'Browse Latest Jobs', subtitle: '2,400+ openings this week', badge: 'HOT', badgeColor: '#FF3B5C', href: '/jobs', icon: <Briefcase size={18} />, iconColor: '#FFB300', reason: 'Popular right now' },
      { id: 'r2', title: 'Classifieds Near You', subtitle: 'Buy & sell in your area', href: '/classifieds', icon: <Package size={18} />, iconColor: '#00C853', reason: 'Trending in Nairobi' },
      { id: 'r3', title: 'Properties for Rent', subtitle: 'Apartments from KES 15,000', href: '/property', icon: <Home size={18} />, iconColor: '#FF9800', reason: 'Most viewed today' },
      { id: 'r4', title: 'Vehicles for Sale', subtitle: 'Cars, bikes & trucks', href: '/vehicles', icon: <Car size={18} />, iconColor: '#2196F3', reason: 'New listings added' },
    ];
  }

  switch (role) {
    case 'seller':
      return [
        { id: 's1', title: 'Post a New Product', subtitle: 'Reach 250K+ buyers', badge: 'QUICK', badgeColor: '#00C853', href: '/create-listing?type=product', icon: <Package size={18} />, iconColor: '#00C853', reason: 'Grow your sales' },
        { id: 's2', title: 'Boost Your Listings', subtitle: 'Get 5x more views', href: '/buy-credits', icon: <TrendingUp size={18} />, iconColor: '#FFB300', reason: 'Sellers like you use this' },
        { id: 's3', title: 'Seller Dashboard', subtitle: 'Track orders & revenue', href: '/dashboard/seller', icon: <Star size={18} />, iconColor: '#9C27B0', reason: 'Your activity hub' },
        { id: 's4', title: 'Offer a Service', subtitle: 'List your skills', href: '/create-listing?type=service', icon: <Wrench size={18} />, iconColor: '#795548', reason: 'High demand services' },
      ];
    case 'employer':
      return [
        { id: 'e1', title: 'Post a Job', subtitle: 'Find talent fast', badge: 'QUICK', badgeColor: '#9C27B0', href: '/create-listing?type=job', icon: <Briefcase size={18} />, iconColor: '#9C27B0', reason: 'Hire today' },
        { id: 'e2', title: 'Browse Job Seekers', subtitle: '18,000+ active candidates', href: '/jobs', icon: <Star size={18} />, iconColor: '#FFB300', reason: 'Find your match' },
        { id: 'e3', title: 'Create an Event', subtitle: 'Workshops, conferences', href: '/create-listing?type=event', icon: <Star size={18} />, iconColor: '#E91E63', reason: 'Engage your team' },
        { id: 'e4', title: 'Employer Dashboard', subtitle: 'Manage applications', href: '/dashboard', icon: <TrendingUp size={18} />, iconColor: '#00C853', reason: 'Your hiring hub' },
      ];
    case 'agent':
      return [
        { id: 'a1', title: 'Post a Property', subtitle: 'List for sale or rent', badge: 'QUICK', badgeColor: '#FF9800', href: '/create-listing?type=property', icon: <Home size={18} />, iconColor: '#FF9800', reason: 'High demand area' },
        { id: 'a2', title: 'Post a Job', subtitle: 'Recruit candidates', href: '/create-listing?type=job', icon: <Briefcase size={18} />, iconColor: '#9C27B0', reason: 'Active job market' },
        { id: 'a3', title: 'Browse Properties', subtitle: 'Latest listings', href: '/property', icon: <Home size={18} />, iconColor: '#FF9800', reason: 'Market overview' },
        { id: 'a4', title: 'Agent Dashboard', subtitle: 'Track placements', href: '/dashboard', icon: <TrendingUp size={18} />, iconColor: '#00C853', reason: 'Your activity hub' },
      ];
    case 'job_seeker':
      return [
        { id: 'j1', title: 'Browse New Jobs', subtitle: '2,400+ openings', badge: 'HOT', badgeColor: '#FF3B5C', href: '/jobs', icon: <Briefcase size={18} />, iconColor: '#FFB300', reason: 'Matches your profile' },
        { id: 'j2', title: 'Offer a Service', subtitle: 'Earn while you search', href: '/create-listing?type=service', icon: <Wrench size={18} />, iconColor: '#795548', reason: 'Earn extra income' },
        { id: 'j3', title: 'Update Your CV', subtitle: 'Stand out to employers', href: '/profile', icon: <Star size={18} />, iconColor: '#00C853', reason: 'Boost your chances' },
        { id: 'j4', title: 'Hotels & Accommodation', subtitle: 'Find a place to stay', href: '/hotels', icon: <Hotel size={18} />, iconColor: '#F44336', reason: 'Relocation support' },
      ];
    default: // buyer
      return [
        { id: 'b1', title: 'Browse Classifieds', subtitle: 'Great deals near you', badge: 'NEW', badgeColor: '#00C853', href: '/classifieds', icon: <Package size={18} />, iconColor: '#00C853', reason: 'Trending in your area' },
        { id: 'b2', title: 'Find a Job', subtitle: '2,400+ openings', href: '/jobs', icon: <Briefcase size={18} />, iconColor: '#FFB300', reason: 'Popular this week' },
        { id: 'b3', title: 'Properties for Rent', subtitle: 'From KES 15,000/mo', href: '/property', icon: <Home size={18} />, iconColor: '#FF9800', reason: 'Most viewed today' },
        { id: 'b4', title: 'Vehicles for Sale', subtitle: 'Cars & bikes', href: '/vehicles', icon: <Car size={18} />, iconColor: '#2196F3', reason: 'New listings added' },
      ];
  }
}

export default function Recommendations() {
  const { user, activeRole, loading } = useAuth();
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  if (!mounted || loading) return null;

  const isLoggedIn = !!user;
  const role = activeRole || 'buyer';
  const items = getRecommendations(role, isLoggedIn);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={16} style={{ color: 'var(--primary)' }} />
          <h2 className="text-base font-700 text-foreground">
            {isLoggedIn ? 'Recommended for You' : 'Trending Now'}
          </h2>
        </div>
        {isLoggedIn && (
          <span className="text-xs px-2 py-0.5 rounded-full font-600 capitalize"
            style={{ background: 'rgba(0,200,83,0.1)', color: 'var(--primary)', border: '1px solid rgba(0,200,83,0.2)' }}>
            {role.replace('_', ' ')}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map(item => (
          <Link key={item.id} href={item.href}
            className="flex items-center gap-3 p-3 rounded-2xl transition-all duration-150 active:scale-[0.98] hover:opacity-90"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: `${item.iconColor}15`, color: item.iconColor }}>
              {item.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-sm font-700 text-foreground truncate">{item.title}</span>
                {item.badge && (
                  <span className="px-1.5 py-0.5 rounded-full text-[9px] font-700 flex-shrink-0"
                    style={{ background: item.badgeColor, color: '#fff' }}>
                    {item.badge}
                  </span>
                )}
              </div>
              <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>{item.subtitle}</p>
              <p className="text-[10px] mt-0.5 font-500" style={{ color: 'var(--primary)' }}>{item.reason}</p>
            </div>
            <ChevronRight size={14} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
          </Link>
        ))}
      </div>

      {/* CTA for guests */}
      {!isLoggedIn && (
        <Link href="/sign-up-login-screen"
          className="mt-3 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-700 transition-all duration-150 active:scale-95"
          style={{ background: 'rgba(0,200,83,0.08)', border: '1px solid rgba(0,200,83,0.25)', color: 'var(--primary)' }}>
          <Sparkles size={15} />
          Sign in for personalised recommendations
        </Link>
      )}
    </div>
  );
}
